import React from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { BookingStatus } from '../interfaces/heavyDutyMachinery/IBooking';
import BookingReferenceTag from './BookingReferenceTag';
import MachineryThumbnail from './MachineryThumbnail';
import { BOOKING_STATUS_LABEL_KEYS, BOOKING_STATUS_TONES } from '../constants/bookingStatusOptions';
import { pillToneStyle, CORAL, CORAL_SOFT_BG } from '../theme/creamyTokens';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

interface BookingCardProps {
  title: string;
  /**
   * The rented machine's cover photo (see IBooking's own
   * heavyDutyMachineryThumbnailUrl). Optional, and absent is ordinary
   * rather than an error -- MachineryThumbnail draws its own fallback, so
   * every card keeps the same shape whether there is a photo or not.
   */
  thumbnailUrl?: string;
  /** What this booking is called, e.g. "R-PPTAR" -- until this existed, two rentals of the same machine could only be told apart by price. */
  reference?: string;
  status: BookingStatus;
  dateRangeLabel: string;
  priceLabel: string;
  extraLine?: string;
  /** Past or cancelled bookings render at reduced opacity to read as inactive at a glance. */
  muted?: boolean;
  // Only set by IncomingBookings -- a booking created since the host's last
  // visit to that list, same "unread until you come back and look" idea as
  // an inbox rather than a fixed time window, which would keep shuffling
  // cards around a host mid-session. Never set on the guest's own "Mis
  // reservas" (MyBookings never passes it) -- a guest already knows which
  // booking they just made.
  isNew?: boolean;
  actions?: React.ReactNode;
}

/**
 * Mobile "Mis reservas" card -- same surface/border/radius language as
 * HeavyDutyMachineryCard's listing cards (see that component), reused here so a
 * booking reads as the same kind of object elsewhere in the app instead of
 * antd List.Item's plain divider-separated rows.
 */
const BookingCard: React.FC<BookingCardProps> = ({
  title,
  thumbnailUrl,
  reference,
  status,
  dateRangeLabel,
  priceLabel,
  extraLine,
  muted,
  isNew,
  actions,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return (
    <div
      style={{
        background: isNew ? CORAL_SOFT_BG : token.colorBgContainer,
        border: `1px solid ${isNew ? CORAL : token.colorBorderSecondary}`,
        borderRadius: 20,
        padding: 16,
        opacity: muted ? 0.6 : 1,
        transition: 'opacity 150ms ease, background-color 150ms ease, border-color 150ms ease',
      }}
    >
      {/* Photo on the LEFT, not the right, although the right was the first
          idea: the card's right edge is already where the status pill and
          the "Nueva" badge live, and those are the other thing people scan
          a booking list for. Two things competing for one corner makes both
          harder to find. On the left the photo lines up with the title and
          the eye reads picture-then-name, which is the order the question
          actually arrives in ("which machine was this one?").

          The photo and the text share a row; the actions below stay full
          width rather than indenting under the text, so buttons keep their
          whole touch target on a phone. */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <MachineryThumbnail url={thumbnailUrl} alt={title} />
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* The title and the badges share a row only while both fit. A long
              status label -- "Presupuesto aceptado, pendiente de pago" is wider
              than a phone -- used to keep its full width (flexShrink: 0) and
              crush the title to a column of single letters. Now the row wraps
              instead: the badges drop to their own line, still right-aligned by
              the auto margin, and the title always gets a whole line to itself.
              justifyContent is deliberately NOT space-between, which on a
              wrapped line of one item would push the badges back to the left. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
            <Text strong style={{ fontSize: 15, flex: '1 1 140px', minWidth: 0 }}>
              {title}
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
              {isNew && (
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#fff',
                    background: CORAL,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_NEW_BADGE)}
                </span>
              )}
              <span
                style={{
                  padding: '3px 10px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  ...pillToneStyle(BOOKING_STATUS_TONES[status], token),
                }}
              >
                {translate(t, BOOKING_STATUS_LABEL_KEYS[status])}
              </span>
        </div>
      </div>

      {/* Directly under the title, above everything else: it is what the
          card IS, not one of its details. */}
      <BookingReferenceTag reference={reference} style={{ display: 'block', marginTop: 2 }} />

      <Text style={{ fontSize: 13, display: 'block', marginTop: 6 }}>{dateRangeLabel}</Text>
      <Text strong style={{ fontSize: 15, display: 'block', marginTop: 6 }}>
        {priceLabel}
      </Text>
      {extraLine && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          {extraLine}
        </Text>
      )}
        </div>
      </div>

      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>{actions}</div>}
    </div>
  );
};

export default BookingCard;
